import React from "react";
import classes from "./HighlightSection.module.scss";
import useIsMobile from "../../../hooks/useIsMobile";
import { getOnboardingOrFormCtaProps } from "../../../utils/applyCtaButtonProps";
import { useRouter } from "next/router";
import { persistSourceContext } from "../../../utils/b2c/sourceTracking";
import { PRIMARY_SOURCE_B2C_WIDGET } from "../../../utils/b2c/constants";
import CustomImage from "../../CustomImage";

const Card = ({ iconUrl, title, description }: any) => {
  return (
    <div className={classes["card-container"]}>
      <div className={classes["icon-container"]}>
        <CustomImage src={iconUrl ?? ""} width={22} height={22} alt="icon" />
      </div>
      <h3 className={classes["title"]}>{title}</h3>
      <p className={classes["description"]}>{description}</p>
    </div>
  );
};

const IconContainer = ({ url }: any) => {
  return (
    <div className={classes["icon-container"]}>
      <CustomImage src={url ?? ""} width={19} height={19} alt="icon" />
    </div>
  );
};

const HighlightSection = ({ featureShowcaseData }: any) => {
  const router = useRouter();
  const isMobile = useIsMobile(1350);
  const card1Data = featureShowcaseData?.highlightedCard?.[0];
  const card2Data = featureShowcaseData?.highlightedCard?.[1];
  const card3Data = featureShowcaseData?.highlightedCard?.[2];
  const card4Data = featureShowcaseData?.highlightedCard?.[3];
  const card5Data = featureShowcaseData?.highlightedCard?.[4];
  const card6Data = featureShowcaseData?.highlightedCard?.[5];

  const applyCtaButtonProps = getOnboardingOrFormCtaProps(router, {
    btnLink: featureShowcaseData?.button?.link,
    formId: featureShowcaseData?.button?.formId,
    toOnboardingEnabled: featureShowcaseData?.button?.toOnboardingEnabled,
    sourceUrl: typeof window !== "undefined" ? window.location.pathname : "",
  });

  const ctaBtnClickHandler = (applyCtaButtonProps: any) => (e: React.MouseEvent<HTMLButtonElement>) => {
    if (applyCtaButtonProps.mode === "onboarding") {
      persistSourceContext({
        sourceUrl: window.location.href,
        primarySource: PRIMARY_SOURCE_B2C_WIDGET,
        fifthStepVisible: featureShowcaseData?.button?.nearestOffice ?? true,
        coachingVisible: featureShowcaseData?.button?.lookingForCoaching ?? true,
      });
    }
    applyCtaButtonProps.onClick?.(e);
  };

  return (
    <>
      {!isMobile ? (
        <section className={classes["highlight-section-container"]}>
          <div className={classes["content-container"]}>
            {featureShowcaseData?.title && <h2 className={classes["title"]}>{featureShowcaseData?.title}</h2>}
            <button onClick={ctaBtnClickHandler(applyCtaButtonProps)} className={`${applyCtaButtonProps.formClassName} ${classes["enquire-now-btn"]}`}>
              {featureShowcaseData?.button?.title || "Enquire Now"}
            </button>

            <div className={classes["orbit-3"]}>
              {featureShowcaseData?.orbit3Icons?.length > 0 &&
                featureShowcaseData?.orbit3Icons?.map((icon: any, index: number) => (
                  <div className={`${classes[`icon-${index + 1}-container`]}`}>
                    <IconContainer url={icon.url ?? ""} />
                  </div>
                ))}
            </div>

            <div className={classes["card-1-container"]}>
              <Card iconUrl={card5Data?.icon?.url} title={card5Data?.title} description={card5Data?.description} />
            </div>
            <div className={classes["card-2-container"]}>
              <Card iconUrl={card6Data?.icon?.url} title={card6Data?.title} description={card6Data?.description} />
            </div>
            <div className={classes["card-3-container"]}>
              <Card iconUrl={card1Data?.icon?.url} title={card1Data?.title} description={card1Data?.description} />
            </div>
            <div className={classes["card-4-container"]}>
              <Card iconUrl={card2Data?.icon?.url} title={card2Data?.title} description={card2Data?.description} />
            </div>
            <div className={classes["card-5-container"]}>
              <Card iconUrl={card3Data?.icon?.url} title={card3Data?.title} description={card3Data?.description} />
            </div>
            <div className={classes["card-6-container"]}>
              <Card iconUrl={card4Data?.icon?.url} title={card4Data?.title} description={card4Data?.description} />
            </div>
          </div>

          <div className={classes["orbit-1"]}>
            {featureShowcaseData?.orbit1Icons?.length > 0 &&
              featureShowcaseData?.orbit1Icons?.map((icon: any, index: number) => (
                <div className={`${classes[`icon-${index + 1}-container`]}`}>
                  <IconContainer url={icon.url ?? ""} />
                </div>
              ))}
          </div>
          <div className={classes["orbit-2"]}>
            {featureShowcaseData?.orbit2Icons?.length > 0 &&
              featureShowcaseData?.orbit2Icons?.map((icon: any, index: number) => (
                <div className={`${classes[`icon-${index + 1}-container`]}`}>
                  <IconContainer url={icon.url ?? ""} />
                </div>
              ))}
          </div>
        </section>
      ) : (
        <section className={classes["highlight-section-container-mobile"]}>
          <h2 className={classes["title"]}>{featureShowcaseData?.title}</h2>
          <div className={classes["cards-container"]}>
            {featureShowcaseData?.highlightedCard?.map((card: any, index: number) => (
              <div className={`${classes["card"]} ${classes[`card-${index + 1}`]}`}>
                <div className={classes["icon-container"]}>
                  <CustomImage width={22} height={22} src={card?.icon?.url} alt={card?.icon?.name} />
                </div>

                <h4 className={classes["title"]}>{card?.title}</h4>
                <p className={classes["description"]}>{card?.description}</p>
              </div>
            ))}
          </div>

          <button onClick={ctaBtnClickHandler(applyCtaButtonProps)} className={`${applyCtaButtonProps.formClassName} ${classes["enquire-now-btn"]}`}>
            {featureShowcaseData?.button?.title || "Enquire Now"}
          </button>
        </section>
      )}
    </>
  );
};

export default HighlightSection;
